import { SaveRefused } from '../saveWithRetry';
import { forgetListReads } from './listReadStatus';
// ─────────────────────────────────────────────────────────────────────
// NO WRITES ACROSS A SCOPE TRANSITION (docs/architecture/AUTHORIZATION.md,
// «Still to build» item 3 — built 2026-10-31).
//
// An administrator who holds roles in two organizations switches between them
// inside the portal. The store is re-keyed on the selected scope (#399), so
// every hook's state is discarded at once — but a hook's ASYNC operation that
// was already running keeps running: `await Storage.get(…)` under club A,
// then the switch, then `await Storage.set(…)` — and the transport, which
// resolves the organization per request, addresses that write to club B.
// A's bookings land in B's calendar, under B's header, and the server answers
// perfectly correctly for the wrong club.
//
// THE TRANSPORT CANNOT KNOW WHEN AN OPERATION BEGAN, only when its request is
// made. What it can know is that a transition is IN PROGRESS: from the moment
// the organization every request is addressed to CHANGES until the surface
// that now owns the screen has loaded its data. During that window no write
// is legitimate — the new store has nothing to write yet, and anything that
// does try is, by construction, the tail of an operation begun under the
// previous organization. So every write is refused in the window, loudly,
// with a typed error the caller can tell apart.
//
// WHERE THE ADDRESS CHANGES, and every one opens the window (💬13 review):
//   • the portal's scope switcher — `clubSession.selectScope`;
//   • an impersonation context starting, switching or ending —
//     `setImpersonationContext` (the operator's own club → the customer's);
//   • leaving the portal for «Min StarPoint» or entering the portal from it —
//     `selectCustomerSurface` (the member surface addresses the player's
//     club, or none);
//   • the player app's 📍 club switch — `selectPlayerClub`.
// The first three resolve the address the way the transport does, through
// `customerContext()`, so the window follows the resolved tenant and not any
// one input. It is CLOSED by the load of whichever surface took over:
// `useInitialLoad` for the portal, `useMemberBootstrap` for the player app.
//
// WHAT THIS DOES NOT CLOSE, stated rather than implied: an operation begun
// under A that is still running after the new surface's data has loaded would
// still land there. Nothing in the portal takes that long between its read and
// its write, and closing it for good means every writer carrying the scope it
// began in — a refactor of every hook, not a transport rule. The window is the
// real-world case; the residue is recorded in AUTHORIZATION.md.
//
// A SAFETY VALVE, because a transition that never ends would be a portal that
// never writes again: if no load reports completion within
// `SCOPE_TRANSITION_TIMEOUT_MS`, the window closes on its own and says so on
// the console. A load that fails still reports completion, so the valve is for
// the unexpected only. Thirty seconds, so a large club on a slow phone is not
// handed a reopened window before its data is in (review, L3); that too is
// recorded with the residue.
// ─────────────────────────────────────────────────────────────────────

/** How long a transition may stay open without the new surface reporting in. */
export const SCOPE_TRANSITION_TIMEOUT_MS = 30_000;

export interface ScopeTransition {
  /** The organization the requests were addressed to before. */
  from: string;
  /** The organization they are addressed to now (null: none — «Min StarPoint»). */
  to: string | null;
  /** `Date.now()` when it began. */
  since: number;
}

/**
 * A write refused because the portal was between two organizations.
 *
 * A `SaveRefused`, NOT a plain error (#1236, RETRO-003): `saveWithRetry`
 * retries everything that is not a refusal, at +150 and +400 ms, and the new
 * organization's load can close this window inside that backoff — so a plain
 * error here was re-issued and addressed to the organization the person had
 * switched TO, which is the one write this guard exists to stop. As a refusal
 * it is an answer: one attempt, rethrown at once, and every caller that shows
 * `e.message` for a `SaveRefused` shows this sentence instead of «could not
 * save». `status` 0 — nothing was sent, there is no HTTP answer.
 */
export class ScopeTransitionError extends SaveRefused {
  override readonly code = 'scope_transition' as const;
  constructor(readonly transition: ScopeTransition) {
    super(
      'Du byttet organisasjon før lagringen var ferdig. Gjør endringen på nytt.',
      0,
      'scope_transition',
    );
    this.name = 'ScopeTransitionError';
  }
}

interface Deps {
  now: () => number;
  setTimeout: (fn: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
  warn: (message: string) => void;
}

const defaultDeps: Deps = {
  now: () => Date.now(),
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
  warn: (m) => console.warn(m),
};

let deps: Deps = defaultDeps;
let active: ScopeTransition | null = null;
let valve: unknown = null;

/**
 * The address every request carries is moving from `from` to `to`. A no-op
 * when nothing changes, and — deliberately — when nothing was addressed
 * before: the first selection after sign-in has no operation of another
 * club's still running, and blocking the landing's own writes would buy
 * nothing. Leaving an organization for NONE does open it: the tail of its
 * operation must not land under the player's club once one is entered.
 */
export function beginScopeTransition(from: string | null, to: string | null): void {
  // Whatever was READ for the previous address no longer describes the
  // screen (#1247): forgotten on every move, the first selection included,
  // even where writes below have nothing to hold.
  if (from !== to) forgetListReads();
  if (from === null || from === to) return;
  if (valve !== null) deps.clearTimeout(valve);
  active = { from, to, since: deps.now() };
  valve = deps.setTimeout(() => {
    if (active === null) return;
    deps.warn(
      `[Padel24] No surface reported its load within ${SCOPE_TRANSITION_TIMEOUT_MS} ms after ` +
        `leaving ${active.from}; writes are allowed again.`,
    );
    endScopeTransition();
  }, SCOPE_TRANSITION_TIMEOUT_MS);
}

/** The surface that took over has its data: writes may flow again. Idempotent. */
export function endScopeTransition(): void {
  if (valve !== null) {
    deps.clearTimeout(valve);
    valve = null;
  }
  active = null;
}

/** The transition in progress, or null. */
export function scopeTransition(): ScopeTransition | null {
  return active;
}

/** Tests only: forget any transition and swap the clock and timers. */
export function __resetScopeTransition(override?: Partial<Deps>): void {
  if (valve !== null) deps.clearTimeout(valve);
  valve = null;
  active = null;
  deps = { ...defaultDeps, ...override };
}
